<template>
  <div class="gm-panel skill-progression-chart">
    <div v-if="!chartData" class="gm-empty">
      <div class="gm-empty__title">No Skill Selected</div>
      <p class="gm-empty__hint">Select a skill to analyze progression</p>
    </div>

    <div v-else class="chart-container">
      <div class="chart-header">
        <h3 class="gm-section-title chart-title">{{ chartData.meta.skillName }}</h3>
        <div class="chart-meta">
          <span class="gm-chip gm-chip--muted">{{ chartData.meta.schoolName }}</span>
          <span class="gm-chip">Primary: {{ chartData.meta.primaryStat }}</span>
        </div>
      </div>
      <canvas ref="chartCanvas"></canvas>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, onMounted, onBeforeUnmount } from 'vue';
import { Chart, registerables } from 'chart.js';

Chart.register(...registerables);

const props = defineProps({
  chartData: {
    type: Object,
    default: null
  }
});

const chartCanvas = ref(null);
let chartInstance = null;

const createChart = () => {
  if (!chartCanvas.value || !props.chartData) return;

  if (chartInstance) {
    chartInstance.destroy();
  }

  const ctx = chartCanvas.value.getContext('2d');
  chartInstance = new Chart(ctx, {
    type: 'line',
    data: {
      labels: props.chartData.labels,
      datasets: props.chartData.datasets
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: true,
          position: 'top',
          labels: {
            color: '#f3e7cf',
            font: {
              size: 11
            },
            usePointStyle: true,
            padding: 10
          }
        },
        tooltip: {
          mode: 'index',
          intersect: false,
          backgroundColor: 'rgba(12, 14, 15, 0.92)',
          titleColor: '#fada95',
          bodyColor: '#f3e7cf',
          borderColor: 'rgba(250, 218, 149, 0.4)',
          borderWidth: 1,
          padding: 10,
          displayColors: true,
          callbacks: {
            title: (tooltipItems) => {
              return `${props.chartData.meta.primaryStat}: ${tooltipItems[0].label}`;
            },
            label: (context) => {
              return `${context.dataset.label}: ${context.parsed.y}`;
            }
          }
        }
      },
      scales: {
        x: {
          title: {
            display: true,
            text: `${props.chartData.meta.primaryStat} Value`,
            color: '#fada95',
            font: {
              size: 12,
              weight: 'bold'
            }
          },
          grid: {
            color: 'rgba(250, 218, 149, 0.08)',
            drawBorder: false
          },
          ticks: {
            color: '#9a9078',
            font: {
              size: 10
            }
          }
        },
        y: {
          title: {
            display: true,
            text: 'Impact Value',
            color: '#fada95',
            font: {
              size: 12,
              weight: 'bold'
            }
          },
          grid: {
            color: 'rgba(250, 218, 149, 0.08)',
            drawBorder: false
          },
          ticks: {
            color: '#9a9078',
            font: {
              size: 10
            }
          },
          beginAtZero: true
        }
      },
      interaction: {
        mode: 'nearest',
        axis: 'x',
        intersect: false
      }
    }
  });
};

watch(() => props.chartData, () => {
  createChart();
}, { deep: true });

onMounted(() => {
  if (props.chartData) {
    createChart();
  }
});

onBeforeUnmount(() => {
  if (chartInstance) {
    chartInstance.destroy();
  }
});
</script>

<style scoped>
.skill-progression-chart {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.chart-container {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.chart-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--gm-gap);
  padding-bottom: var(--gm-gap-sm);
  border-bottom: 1px solid var(--gm-border-dim);
}

.chart-title {
  margin: 0;
}

.chart-meta {
  display: flex;
  gap: var(--gm-gap-xs);
}

canvas {
  flex: 1;
  max-height: calc(100% - 80px);
}
</style>
